import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import BasicCli from "../../../main/components/basicCli/BasicCli";
import GeneralModal from "../../../main/components/modal/general/GeneralModal";
import axiosInstance from "../../../main/initializers/axios";
import { ApiConstants } from "../../../main/constants/ApiConstants";
import { BookingRequestStatus } from "../../../main/assets/enums/eBookingRequestStatus";
import IPagination from "../../../main/interfaces/IPagination";
import IGridItems from "../../../main/interfaces/IGridItems";
import IBookingRequest from "../../../main/interfaces/IBookingRequest";
import useGetUser from "../../../main/hooks/useGetUser";
import "./BookingRequests.css";

type BookingRequestGridItem = IBookingRequest & {
  statusLabel: string;
  profileName: string;
  hotelName: string;
  roomTypeName: string;
  roomNumber: string;
  requestedRoomId?: number;
  requestedRoom?: {
    id: number;
    roomNumber: string;
  };
  created?: string;
  cancelNote?: string;
};

const defaultPagination: IPagination = {
  page: 1,
  itemPerPage: 50,
  count: 0,
  itemsPerPage: [10, 20, 50, 100],
};

const defaultColumns: IGridItems[] = [
  { entity: "profileName", label: "Profile", isShown: true },
  { entity: "hotel.name", label: "Hotel", isShown: true },
  { entity: "requestedRoomType.name", label: "RoomType", isShown: true },
  { entity: "checkInDate", label: "CheckIn", isShown: true },
  { entity: "checkOutDate", label: "CheckOut", isShown: true },
  { entity: "status", label: "Status", isShown: true },
];

const getStatusLabel = (status: number) => {
  switch (status) {
    case BookingRequestStatus.Pending:
      return "Pending";
    case BookingRequestStatus.Approved:
      return "Approved";
    case BookingRequestStatus.Rejected:
      return "Rejected";
    case BookingRequestStatus.Cancelled:
      return "Cancelled";
    case BookingRequestStatus.Expired:
      return "Expired";
    default:
      return "Unknown";
  }
};

const toInputDate = (value: Date) => {
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, "0");
  const day = `${value.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDate = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
};

const formatDateTime = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const extractCancelNote = (specialRequests?: string) => {
  if (!specialRequests) return "";

  const lines = specialRequests.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const noteLine = lines.find((line) => line.toLowerCase().startsWith("cancellation note:"));
  return noteLine ? noteLine.replace(/^cancellation note:\s*/i, "").trim() : "";
};

const getStatusFilterLabel = (status: string) => {
  switch (status) {
    case "2":
      return "Approved";
    case "4":
      return "Cancelled";
    case "1":
      return "Pending";
    default:
      return "All statuses";
  }
};

const BookingRequests = () => {
  const navigate = useNavigate();
  const currentUser = useGetUser();
  const [items, setItems] = useState<BookingRequestGridItem[]>([]);
  const [pagination, setPagination] = useState<IPagination>(defaultPagination);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [fromDate, setFromDate] = useState<string>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return toInputDate(date);
  });
  const [toDate, setToDate] = useState<string>(() => toInputDate(new Date()));
  const [cancelModalItem, setCancelModalItem] = useState<BookingRequestGridItem | null>(null);
  const [cancelNote, setCancelNote] = useState("");
  const [approvedModalItem, setApprovedModalItem] = useState<BookingRequestGridItem | null>(null);

  const roleCode = (currentUser?.role?.code ?? "").toLowerCase();
  const isStaff =
    !!currentUser?.role?.isAdministrator ||
    ["admin", "administrator", "staff", "manager", "reception", "receptionist", "superadmin"].includes(roleCode);

  const mapItem = (x: IBookingRequest): BookingRequestGridItem => ({
    ...x,
    id: Number((x as any)?.id ?? (x as any)?.Id ?? 0),
    hotelId: Number((x as any)?.hotelId ?? (x as any)?.HotelId ?? 0),
    userId: Number((x as any)?.userId ?? (x as any)?.UserId ?? 0),
    requestedRoomTypeId: Number((x as any)?.requestedRoomTypeId ?? (x as any)?.RequestedRoomTypeId ?? 0),
    requestedRoomId: (x as any)?.requestedRoomId ?? (x as any)?.RequestedRoomId,
    requestedRatePlanId: (x as any)?.requestedRatePlanId ?? (x as any)?.RequestedRatePlanId,
    checkInDate: (x as any)?.checkInDate ?? (x as any)?.CheckInDate ?? "",
    checkOutDate: (x as any)?.checkOutDate ?? (x as any)?.CheckOutDate ?? "",
    adults: Number((x as any)?.adults ?? (x as any)?.Adults ?? 0),
    children: Number((x as any)?.children ?? (x as any)?.Children ?? 0),
    specialRequests: (x as any)?.specialRequests ?? (x as any)?.SpecialRequests ?? "",
    source: (x as any)?.source ?? (x as any)?.Source ?? "",
    status: Number((x as any)?.status ?? (x as any)?.Status ?? 0),
    user: (x as any)?.user ?? (x as any)?.User,
    hotel: (x as any)?.hotel ?? (x as any)?.Hotel,
    requestedRoomType: (x as any)?.requestedRoomType ?? (x as any)?.RequestedRoomType,
    requestedRoom: (x as any)?.requestedRoom ?? (x as any)?.RequestedRoom,
    created: (x as any)?.created ?? (x as any)?.Created ?? "",
    profileName: `${(x as any)?.user?.firstName ?? (x as any)?.User?.FirstName ?? ""} ${(x as any)?.user?.lastName ?? (x as any)?.User?.LastName ?? ""}`.trim(),
    hotelName: (x as any)?.hotel?.name ?? (x as any)?.Hotel?.Name ?? "-",
    roomTypeName: (x as any)?.requestedRoomType?.name ?? (x as any)?.RequestedRoomType?.Name ?? "-",
    roomNumber: (x as any)?.requestedRoom?.roomNumber ?? (x as any)?.RequestedRoom?.RoomNumber ?? "",
    statusLabel: getStatusLabel(Number((x as any)?.status ?? (x as any)?.Status ?? 0)),
    cancelNote: extractCancelNote((x as any)?.specialRequests ?? (x as any)?.SpecialRequests ?? ""),
  });

  const loadData = useCallback(async (pg?: IPagination, itemsGrid?: IGridItems[]) => {
    try {
      setLoading(true);
      const request = {
        pagination: pg ?? defaultPagination,
        gridItems: itemsGrid ?? defaultColumns,
      };

      const res = await axiosInstance.post(ApiConstants.bookingRequest.bookingRequestListCt, request);
      const mapped: BookingRequestGridItem[] = (res.data.items || []).map(mapItem);

      setItems(mapped);
      setPagination({
        ...defaultPagination,
        ...(res.data.pagination || {}),
      });
    } catch (err: any) {
      toast.error(err?.response?.data || "Failed to load booking requests");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(defaultPagination, defaultColumns);
  }, [loadData]);

  const updateStatus = async (item: BookingRequestGridItem, status: BookingRequestStatus, note?: string) => {
    if (!isStaff) {
      toast.error("Only staff can update booking requests.");
      return;
    }

    if (item.status !== BookingRequestStatus.Pending) {
      toast.warning("Only pending requests can be updated.");
      return;
    }

    try {
      setBusyId(item.id);
      await axiosInstance.put(ApiConstants.bookingRequest.bookingRequestUpdateStatus, {
        id: item.id,
        status,
        note: note?.trim() || undefined,
      });

      toast.success("Request status updated");
      await loadData(defaultPagination, defaultColumns);

      if (status === BookingRequestStatus.Approved) {
        setApprovedModalItem(item);
      }
    } catch (err: any) {
      toast.error(err?.response?.data || "Status update failed");
    } finally {
      setBusyId(null);
    }
  };

  const stats = useMemo(() => {
    return {
      total: items.length,
      pending: items.filter((x) => x.status === BookingRequestStatus.Pending).length,
      approved: items.filter((x) => x.status === BookingRequestStatus.Approved).length,
      cancelled: items.filter((x) => x.status === BookingRequestStatus.Cancelled).length,
    };
  }, [items]);

  const pendingItems = useMemo(
    () => items.filter((item) => item.status === BookingRequestStatus.Pending),
    [items]
  );

  const filteredHistory = useMemo(() => {
    return items.filter((item) => {
      if (statusFilter !== "all" && Number(statusFilter) !== item.status) {
        return false;
      }

      const createdDate = item.created ? new Date(item.created) : null;
      if (!createdDate || Number.isNaN(createdDate.getTime())) {
        return false;
      }

      if (fromDate) {
        const from = new Date(`${fromDate}T00:00:00`);
        if (createdDate < from) {
          return false;
        }
      }

      if (toDate) {
        const to = new Date(`${toDate}T23:59:59`);
        if (createdDate > to) {
          return false;
        }
      }

      return true;
    });
  }, [items, statusFilter, fromDate, toDate]);

  const openCancelModal = (item: BookingRequestGridItem) => {
    setCancelModalItem(item);
    setCancelNote("");
  };

  const closeCancelModal = () => {
    setCancelModalItem(null);
    setCancelNote("");
  };

  const submitCancel = async () => {
    if (!cancelModalItem) {
      return;
    }

    await updateStatus(cancelModalItem, BookingRequestStatus.Cancelled, cancelNote);
    closeCancelModal();
  };

  const closeApprovedModal = () => {
    setApprovedModalItem(null);
  };

  const goToReservations = () => {
    if (!approvedModalItem) {
      return;
    }

    navigate("/reservation", {
      state: {
        selectedHotelId: approvedModalItem.hotelId,
        refreshReservationAt: Date.now(),
        bookingRequestId: approvedModalItem.id,
      },
    });
  };

  return (
    <BasicCli>
      <div className="booking-requests-page">
        <div className="booking-requests-header">
          <div>
            <span className="booking-requests-kicker">Hotel Requests</span>
            <h1>Booking Requests</h1>
            <p>Prano ose anulo kerkesat e rezervimeve qe vijne nga faqja online.</p>
          </div>
        </div>

        <div className="booking-requests-stats">
          <div className="booking-requests-stat">
            <strong>{stats.total}</strong>
            <span>Total</span>
          </div>
          <div className="booking-requests-stat">
            <strong>{stats.pending}</strong>
            <span>Pending</span>
          </div>
          <div className="booking-requests-stat">
            <strong>{stats.approved}</strong>
            <span>Approved</span>
          </div>
          <div className="booking-requests-stat">
            <strong>{stats.cancelled}</strong>
            <span>Cancelled</span>
          </div>
        </div>

        <div className="booking-requests-board">
          <div className="booking-requests-board-head">
            <div>
              <h2>Pending requests</h2>
              <p>Vetem kerkesat ne pritje shfaqen ketu derisa te aprovohen ose anulohen.</p>
            </div>
          </div>

          {loading && <div className="booking-requests-empty">Loading booking requests...</div>}

          {!loading && pendingItems.length === 0 && (
            <div className="booking-requests-empty">No pending booking requests.</div>
          )}

          {!loading &&
            pendingItems.map((item) => {
              const isBusy = busyId === item.id;

              return (
                <div key={item.id} className="booking-request-card">
                  <div className="booking-request-card-top">
                    <div className="booking-request-card-title">
                      <strong>{item.profileName || "Profile"}</strong>
                      <span>{item.hotelName}</span>
                    </div>
                    <div className="booking-request-card-tools">
                      <div className={`booking-request-status status-${item.status}`}>{item.statusLabel}</div>
                      <div className="booking-request-actions booking-request-actions--compact">
                        <button
                          type="button"
                          className="booking-request-btn booking-request-btn-approve"
                          disabled={!isStaff || isBusy}
                          onClick={() => updateStatus(item, BookingRequestStatus.Approved)}
                        >
                          {isBusy ? "Updating..." : "Approve"}
                        </button>
                        <button
                          type="button"
                          className="booking-request-btn booking-request-btn-cancel"
                          disabled={!isStaff || isBusy}
                          onClick={() => openCancelModal(item)}
                        >
                          {isBusy ? "Updating..." : "Cancel"}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="booking-request-grid">
                    <div>
                      <label>Room</label>
                      <span>{item.roomNumber || "-"}</span>
                    </div>
                    <div>
                      <label>Room type</label>
                      <span>{item.roomTypeName}</span>
                    </div>
                    <div>
                      <label>Stay</label>
                      <span>
                        {formatDate(item.checkInDate)} - {formatDate(item.checkOutDate)}
                      </span>
                    </div>
                    <div>
                      <label>Guests</label>
                      <span>
                        {item.adults} adults / {item.children} children
                      </span>
                    </div>
                    <div>
                      <label>Source</label>
                      <span>{item.source || "Online"}</span>
                    </div>
                    {item.specialRequests ? (
                      <div className="booking-request-wide">
                        <label>Special requests</label>
                        <span>{item.specialRequests}</span>
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
        </div>

        <div className="booking-requests-history">
          <div className="booking-requests-history-head">
            <div>
              <span className="booking-requests-kicker">History</span>
              <h2>Rezervime dhe anulime</h2>
              <p>
                Zgjidh periudhen dhe statusin qe do te shohesh. Aktualisht po sheh{" "}
                <strong>{filteredHistory.length}</strong> rezultate.
              </p>
            </div>
          </div>

          <div className="booking-requests-filters">
            <div className="booking-requests-filter">
              <label>From date</label>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </div>
            <div className="booking-requests-filter">
              <label>To date</label>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>
            <div className="booking-requests-filter">
              <label>Status</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">All statuses</option>
                <option value={String(BookingRequestStatus.Pending)}>Pending</option>
                <option value={String(BookingRequestStatus.Approved)}>Approved</option>
                <option value={String(BookingRequestStatus.Cancelled)}>Cancelled</option>
              </select>
            </div>
            
          </div>

          <div className="booking-requests-table-wrap">
            <table className="booking-requests-table">
              <thead>
                <tr>
                  <th>Profile</th>
                  <th>Hotel</th>
                  <th>Room</th>
                  <th>Room Type</th>
                  <th>Stay</th>
                  <th>Created</th>
                  <th>Status</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {!loading && filteredHistory.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="booking-requests-table-empty">
                      No results for the selected period and status.
                    </td>
                  </tr>
                ) : (
                  filteredHistory.map((item) => (
                    <tr key={`history-${item.id}`}>
                      <td>
                        <strong>{item.profileName || "Profile"}</strong>
                      </td>
                      <td>{item.hotelName}</td>
                      <td>{item.roomNumber || "-"}</td>
                      <td>{item.roomTypeName}</td>
                      <td>
                        {formatDate(item.checkInDate)} - {formatDate(item.checkOutDate)}
                      </td>
                      <td>{formatDateTime(item.created)}</td>
                      <td>
                        <span className={`booking-request-status status-${item.status}`}>{item.statusLabel}</span>
                      </td>
                      <td>{item.cancelNote || item.specialRequests || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {pagination?.count ? (
          <div className="booking-requests-footer">Total records: {pagination.count}</div>
        ) : null}
      </div>

      {cancelModalItem && (
        <GeneralModal
          handleClose={closeCancelModal}
          headerName="Confirm cancellation"
          width="560px"
          radius="18px"
          hideFooter
          variant="warning"
        >
          <div className="booking-request-cancel-modal">
            <p>
              Jeni i sigurt qe doni te anuloni kerkesen per <strong>{cancelModalItem.profileName}</strong>?
            </p>
            <div className="booking-request-cancel-field">
              <label>Cancellation note (optional)</label>
              <textarea
                value={cancelNote}
                onChange={(e) => setCancelNote(e.target.value)}
                placeholder="Shkruaj arsyen e anulimit nese deshiron..."
                rows={4}
              />
            </div>
            <div className="booking-request-cancel-actions">
              <button type="button" className="booking-request-btn booking-request-btn-ghost" onClick={closeCancelModal}>
                Mbyll
              </button>
              <button
                type="button"
                className="booking-request-btn booking-request-btn-cancel-solid"
                onClick={submitCancel}
                disabled={busyId === cancelModalItem.id}
              >
                {busyId === cancelModalItem.id ? "Cancelling..." : "Po, anuloje"}
              </button>
            </div>
          </div>
        </GeneralModal>
      )}

      {approvedModalItem && (
        <GeneralModal
          handleClose={closeApprovedModal}
          headerName="Reservation approved"
          width="520px"
          radius="18px"
          hideFooter
          variant="default"
        >
          <div className="booking-request-approve-modal">
            <p>
              Rezervimi u aprovua me sukses. A do te shkosh te faqja e rezervimeve?
            </p>
            <div className="booking-request-cancel-actions">
              <button
                type="button"
                className="booking-request-btn booking-request-btn-ghost"
                onClick={closeApprovedModal}
              >
                Jo
              </button>
              <button
                type="button"
                className="booking-request-btn booking-request-btn-approve"
                onClick={goToReservations}
              >
               Po
              </button>
            </div>
          </div>
        </GeneralModal>
      )}
    </BasicCli>
  );
};

export default BookingRequests;
